// modules/ads-stats/AdsStatsPage.tsx
// ДОМЕН 5 — Звіт «По оголошеннях» з сортованою таблицею

import React, { useState } from 'react';
import { useAdsStats, getPresets, type SortField, type DateRange, type TabKey } from './useAdsStats.ts';
import { AccountFilterModal } from './AccountFilterModal.tsx';
import { CopyForSheetsModal } from './CopyForSheetsModal.tsx';
import { exportAdsReportToXlsx } from '../../core/xlsxExport.ts';
import { api, ApiClientError } from '../../core/apiClient.ts';
import { useToast } from '../../core/contexts/ToastContext.tsx';

// ─── Column visibility ─────────────────────────────────────────

type ColKey =
  | 'ad_name' | 'ad_status' | 'campaign_name' | 'ad_account_id'
  | 'spend' | 'impressions' | 'clicks' | 'cpm' | 'cpc' | 'ctr'
  | 'leads' | 'cost_per_lead'
  | 'registrations' | 'cost_per_registration'
  | 'purchases' | 'cost_per_purchase'
  | 'app_installs' | 'cost_per_app_install'
  | 'budget';

const DEFAULT_VISIBLE_PER_TAB: Record<TabKey, ColKey[]> = {
  ads: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase', 'budget'
  ],
  campaigns: [
    'campaign_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase', 'budget'
  ],
  geo: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase',
  ],
  accounts: [
    'ad_name', 'ad_status', 'ad_account_id',
    'spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr',
    'leads', 'cost_per_lead',
    'registrations', 'cost_per_registration',
    'purchases', 'cost_per_purchase',
  ],
};

// ─── Number formatters ──────────────────────────────────────────

const fmtThousands = (n: number): string =>
  Math.round(n).toLocaleString('uk-UA');

const fmtMoney = (n: number): string =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPercent = (n: number): string =>
  `${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// ─── Column definitions ─────────────────────────────────────────

interface ColDef {
  key: ColKey;
  field: SortField;
  label: string;
  align: 'left' | 'right';
  render: (row: any) => React.ReactNode;
}

const ALL_COLUMNS: ColDef[] = [
  {
    key: 'ad_name',
    field: 'ad_name',
    label: 'Ad Name',
    align: 'left',
    render: (row) => (
      <span title={row.ad_name ?? row.ad_id} style={{ maxWidth: 240, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.ad_name ?? <span style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{row.ad_id}</span>}
      </span>
    ),
  },
  {
    key: 'ad_status',
    field: 'ad_status',
    label: 'Status',
    align: 'left',
    render: (row) => {
      if (!row.ad_status) return <span style={{ color: 'var(--color-text-subtle)' }}>—</span>;
      let color = 'var(--color-text-muted)';
      if (row.ad_status === 'ACTIVE') color = 'var(--color-success)';
      if (row.ad_status === 'PAUSED') color = 'var(--color-warning)';
      if (row.ad_status === 'DISAPPROVED' || row.ad_status === 'BANNED' || row.ad_status === 'WITH_ISSUES' || row.ad_status === 'Policy') color = 'var(--color-danger)';

      return (
        <span style={{
          fontSize: '0.75rem',
          padding: '2px 6px',
          borderRadius: '4px',
          background: `color-mix(in srgb, ${color} 15%, transparent)`,
          color,
          fontWeight: 600,
          whiteSpace: 'nowrap'
        }}>
          {row.ad_status}
        </span>
      );
    },
  },
  {
    key: 'campaign_name',
    field: 'campaign_name',
    label: 'Campaign',
    align: 'left',
    render: (row) => (
      <span title={row.campaign_name ?? undefined} style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>
        {row.campaign_name ?? '—'}
      </span>
    ),
  },
  {
    key: 'ad_account_id',
    field: 'ad_account_id',
    label: 'Account ID',
    align: 'left',
    render: (row) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }} title={row.ad_account_name ?? undefined}>
        {row.ad_account_id}
      </span>
    ),
  },
  {
    key: 'spend',
    field: 'spend',
    label: 'Spend',
    align: 'right',
    render: (row) => (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--color-accent)' }}>
        {fmtMoney(row.spend)}
      </span>
    ),
  },
  {
    key: 'impressions',
    field: 'impressions',
    label: 'Impr.',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtThousands(row.impressions)}</span>,
  },
  {
    key: 'clicks',
    field: 'clicks',
    label: 'Clicks',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtThousands(row.clicks)}</span>,
  },
  {
    key: 'cpm',
    field: 'cpm',
    label: 'CPM',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>{fmtMoney(row.cpm)}</span>,
  },
  {
    key: 'cpc',
    field: 'cpc',
    label: 'CPC',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}>{fmtMoney(row.cpc)}</span>,
  },
  {
    key: 'ctr',
    field: 'ctr',
    label: 'CTR',
    align: 'right',
    render: (row) => (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.ctr > 1 ? 'var(--color-success)' : 'inherit' }}>
        {fmtPercent(row.ctr)}
      </span>
    ),
  },
  {
    key: 'leads',
    field: 'leads',
    label: 'Leads',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.leads > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.leads)}</span>,
  },
  {
    key: 'cost_per_lead',
    field: 'cost_per_lead',
    label: 'CPL',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.leads > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.leads > 0 ? fmtMoney(row.cost_per_lead) : '—'}</span>,
  },
  {
    key: 'registrations',
    field: 'registrations',
    label: 'Regs',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.registrations > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.registrations)}</span>,
  },
  {
    key: 'cost_per_registration',
    field: 'cost_per_registration',
    label: 'CPReg',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.registrations > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.registrations > 0 ? fmtMoney(row.cost_per_registration) : '—'}</span>,
  },
  {
    key: 'purchases',
    field: 'purchases',
    label: 'Purchases',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: row.purchases > 0 ? 'var(--color-text)' : 'var(--color-text-subtle)' }}>{fmtThousands(row.purchases)}</span>,
  },
  {
    key: 'cost_per_purchase',
    field: 'cost_per_purchase',
    label: 'CPP',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: row.purchases > 0 ? 'inherit' : 'var(--color-text-subtle)' }}>{row.purchases > 0 ? fmtMoney(row.cost_per_purchase) : '—'}</span>,
  },
  {
    key: 'app_installs',
    field: 'app_installs',
    label: 'App Inst.',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-subtle)' }}>{fmtThousands(row.app_installs)}</span>,
  },
  {
    key: 'cost_per_app_install',
    field: 'cost_per_app_install',
    label: 'CPI',
    align: 'right',
    render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}>—</span>,
  },
  {
    key: 'budget',
    field: 'campaign_budget', // just a placeholder field
    label: 'Budget',
    align: 'right',
    render: (row) => null, // rendering is handled specially in the component
  }
];

// ─── Sort arrow ─────────────────────────────────────────────────

function SortArrow({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  return (
    <span style={{ display: 'inline-block', marginLeft: 3, opacity: active ? 1 : 0.25, fontSize: '0.65rem' }}>
      {active && direction === 'asc' ? '▲' : '▼'}
    </span>
  );
}

// ─── Preset buttons ─────────────────────────────────────────────

const PRESET_LABELS: Record<string, string> = {
  today: 'Сьогодні',
  yesterday: 'Вчора',
  last7: '7 днів',
  last30: '30 днів',
};

// ─── Main page ──────────────────────────────────────────────────

const getCurrencySymbol = (currencyCode: string | null) => {
  if (currencyCode === 'USD') return '$';
  if (currencyCode === 'EUR') return '€';
  if (currencyCode === 'UAH') return '₴';
  return currencyCode || '';
};

export function AdsStatsPage() {
  const {
    activeTab,
    setActiveTab,
    report,
    fetchAllReports,
    loading,
    syncing,
    lastSync,
    dateRange,
    setDateRange,
    sync,
    patchReportRow,
    sortConfig,
    toggleSort,
    accountFilter,
    setAccountFilter,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    metricFilters,
    setMetricFilters,
  } = useAdsStats();

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [visibleColsState, setVisibleColsState] = useState<Record<TabKey, Set<ColKey>>>({
    ads: new Set(DEFAULT_VISIBLE_PER_TAB.ads),
    campaigns: new Set(DEFAULT_VISIBLE_PER_TAB.campaigns),
    geo: new Set(DEFAULT_VISIBLE_PER_TAB.geo),
    accounts: new Set(DEFAULT_VISIBLE_PER_TAB.accounts),
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<{ type: 'status' } | { type: 'metric', index: number } | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [editingBudgetRowId, setEditingBudgetRowId] = useState<string | null>(null);
  const [budgetInputValue, setBudgetInputValue] = useState<string>('');

  const { showToast } = useToast();

  /**
   * Повертає СПРАВЖНІЙ Facebook object id для рядка (число), або null,
   * якщо для цього рядка/вкладки немає керованого FB-об'єкта.
   *
   * ВАЖЛИВО: `row.ad_id` для вкладок campaigns/geo/accounts — синтетичний
   * ключ (для React key і вибору рядка в таблиці), а не Facebook id.
   * Ніколи не відправляй row.ad_id у update-status/update-budget для цих вкладок.
   */
  const getActionableFbId = (row: any): string | null => {
    if (activeTab === 'ads') {
      return row.ad_id || null;
    }
    if (activeTab === 'campaigns') {
      return row.campaign_id || null; // null якщо FB не повернув campaign_id — дія недоступна
    }
    // geo / accounts — немає єдиного керованого FB-об'єкта в цьому рядку
    return null;
  };

  const handleAction = async (action: 'ACTIVE' | 'PAUSED') => {
    setActionsMenuOpen(false);
    let successCount = 0;
    const succeededIds: string[] = [];
    const idType = activeTab === 'campaigns' ? 'campaign' : 'ad';
    for (const id of selectedIds) {
      try {
        await api.post('/api/ads-stats/update-status', { id, status: action, idType });
        successCount++;
        succeededIds.push(id);
      } catch (err) {
        showToast(`Помилка для ${id}: ${err instanceof ApiClientError ? err.message : String(err)}`, 'error');
      }
    }
    if (successCount > 0) {
      showToast(`Оновлено статус для ${successCount} елементів`, 'success');
      // Facebook вже підтвердив зміну — просто оновлюємо локальний стан рядків,
      // без важкого повного sync() по всіх кабінетах.
      const matchField = activeTab === 'campaigns' ? 'campaign_id' : 'ad_id';
      succeededIds.forEach((id) => patchReportRow(activeTab, matchField, id, { ad_status: action }));
    }
    setSelectedIds(new Set());
  };

  const handleBudgetSave = async (id: string, type: 'daily' | 'lifetime', matchField: 'campaign_id' | 'adset_id') => {
    const val = Number(budgetInputValue);
    if (isNaN(val) || val <= 0) {
      showToast('Невірне значення бюджету', 'error');
      return;
    }
    const idType = matchField === 'campaign_id' ? 'campaign' : 'adset';
    try {
      await api.post('/api/ads-stats/update-budget', { id, budget: val, type, idType });
      showToast('Бюджет оновлено', 'success');
      setEditingBudgetRowId(null);
      // Facebook вже підтвердив зміну — оновлюємо локально відповідне поле
      // бюджету (для adset_id це може зачепити кілька рядків-оголошень одразу),
      // без важкого повного sync() по всіх кабінетах.
      if (activeTab === 'campaigns') {
        patchReportRow('campaigns', 'campaign_id', id, { campaign_budget: val, campaign_budget_type: type });
      } else if (activeTab === 'ads') {
        patchReportRow('ads', matchField, id, { adset_budget: val, adset_budget_type: type });
      }
    } catch (err) {
      showToast(`Помилка: ${err instanceof ApiClientError ? err.message : String(err)}`, 'error');
    }
  };

  const filterCount = accountFilter ? accountFilter.length : 0;
  const presets = getPresets();
  const rawCurrency = report[0]?.currency ?? null;
  const currencySymbol = getCurrencySymbol(rawCurrency);

  const visibleCols = visibleColsState[activeTab];
  const visibleColumns = ALL_COLUMNS.filter((c) => visibleCols.has(c.key));

  const toggleCol = (c: ColKey) => {
    setVisibleColsState(prev => {
      const nextTabCols = new Set(prev[activeTab]);
      if (nextTabCols.has(c)) nextTabCols.delete(c);
      else nextTabCols.add(c);
      return { ...prev, [activeTab]: nextTabCols };
    });
  };

  // Totals
  const totalSpend = report.reduce((s, r) => s + r.spend, 0);
  const totalImpr = report.reduce((s, r) => s + r.impressions, 0);
  const totalClicks = report.reduce((s, r) => s + r.clicks, 0);
  const totalLeads = report.reduce((s, r) => s + r.leads, 0);
  const totalRegs = report.reduce((s, r) => s + r.registrations, 0);
  const totalPurchases = report.reduce((s, r) => s + r.purchases, 0);

  const totals: Record<ColKey, React.ReactNode> = {
    ad_name: <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Разом ({report.length} оголошень)</span>,
    ad_status: null,
    campaign_name: null,
    ad_account_id: null,
    spend: <span style={{ color: 'var(--color-accent)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalSpend)}</span>,
    impressions: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalImpr)}</span>,
    clicks: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalClicks)}</span>,
    cpm: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalImpr > 0 ? (totalSpend / totalImpr) * 1000 : 0)}</span>,
    cpc: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalClicks > 0 ? totalSpend / totalClicks : 0)}</span>,
    ctr: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPercent(totalImpr > 0 ? (totalClicks / totalImpr) * 100 : 0)}</span>,
    leads: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalLeads)}</span>,
    cost_per_lead: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalLeads > 0 ? fmtMoney(totalSpend / totalLeads) : '—'}</span>,
    registrations: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalRegs)}</span>,
    cost_per_registration: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalRegs > 0 ? fmtMoney(totalSpend / totalRegs) : '—'}</span>,
    purchases: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtThousands(totalPurchases)}</span>,
    cost_per_purchase: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{totalPurchases > 0 ? fmtMoney(totalSpend / totalPurchases) : '—'}</span>,
    app_installs: <span style={{ color: 'var(--color-text-subtle)' }}>—</span>,
    cost_per_app_install: <span style={{ color: 'var(--color-text-subtle)' }}>—</span>,
    budget: null,
  };

  const handleExport = async () => {
    const allReports = await fetchAllReports();
    exportAdsReportToXlsx(allReports, dateRange.from, dateRange.to);
  };

  return (
    <div className="page-body">
      {/* ── Page header ─────────────────────────────────── */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>По оголошеннях</h1>
        <p className="text-muted text-sm">Статистика по рекламним оголошенням Facebook Ads</p>
      </div>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <div
        className="glass-card"
        style={{
          padding: 'var(--space-3) var(--space-4)',
          marginBottom: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          {/* Preset buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {Object.entries(PRESET_LABELS).map(([key, label]) => {
              const preset = presets[key as keyof typeof presets];
              const isActive = dateRange.from === preset.from && dateRange.to === preset.to;
              return (
                <button
                  key={key}
                  className={`btn ${isActive ? 'btn--primary' : 'btn--ghost'}`}
                  style={{ padding: '4px 10px', fontSize: '0.8125rem' }}
                  onClick={() => setDateRange(preset)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Date range inputs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              id="ads-date-from"
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="filter-select"
              style={{ minWidth: 140 }}
            />
            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>—</span>
            <input
              id="ads-date-to"
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="filter-select"
              style={{ minWidth: 140 }}
            />
          </div>

          {/* Account filter button */}
          <button
            id="accounts-filter-btn"
            className={`btn ${filterCount > 0 ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => setFilterModalOpen(true)}
            style={filterCount > 0 ? { boxShadow: '0 0 10px var(--color-primary-glow)' } : {}}
          >
            Список акаунтів
            {filterCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-accent)', color: '#000',
                fontSize: '0.7rem', fontWeight: 700,
                borderRadius: 'var(--radius-full)', minWidth: 18, height: 18,
                padding: '0 5px', marginLeft: 'var(--space-1)',
              }}>
                {filterCount}
              </span>
            )}
          </button>

          {/* Filters Toggle Button */}
          <button
            className={`btn ${statusFilter.length > 0 || metricFilters.length > 0 ? 'btn--primary' : 'btn--ghost'}`}
            style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
            onClick={() => setFiltersPanelOpen(!filtersPanelOpen)}
          >
            Фільтри {(statusFilter.length > 0 || metricFilters.length > 0) && `(${statusFilter.length + metricFilters.length})`}
          </button>

          {/* Actions Menu Toggle */}
          <div style={{ position: 'relative' }}>
            <button
              className={`btn ${selectedIds.size > 0 ? 'btn--primary' : 'btn--ghost'}`}
              style={{ fontSize: '0.8125rem', padding: '4px 10px' }}
              disabled={selectedIds.size === 0}
              title={activeTab === 'geo' || activeTab === 'accounts' ? 'Дії недоступні для цього звіту' : undefined}
              onClick={() => setActionsMenuOpen(!actionsMenuOpen)}
            >
              Actions {selectedIds.size > 0 && `(${selectedIds.size})`}
            </button>
            {actionsMenuOpen && selectedIds.size > 0 && (
              <div
                className="glass-card"
                style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100,
                  padding: 'var(--space-2) 0', width: 160,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  display: 'flex', flexDirection: 'column'
                }}
              >
                <button
                  className="btn btn--ghost"
                  style={{ textAlign: 'left', borderRadius: 0, padding: '8px 16px', justifyContent: 'flex-start' }}
                  onClick={() => handleAction('ACTIVE')}
                >
                  Увімкнути
                </button>
                <button
                  className="btn btn--ghost"
                  style={{ textAlign: 'left', borderRadius: 0, padding: '8px 16px', justifyContent: 'flex-start' }}
                  onClick={() => handleAction('PAUSED')}
                >
                  Вимкнути
                </button>
              </div>
            )}
          </div>

          {/* Column picker toggle */}
          <button
            className="btn btn--ghost"
            style={{ fontSize: '0.8125rem', padding: '4px 10px', position: 'relative' }}
            onClick={() => setColPickerOpen(!colPickerOpen)}
          >
            Колонки
          </button>

          {/* Export and Sync buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-2)' }}>
            {activeTab === 'campaigns' && (
              <button
                className="btn btn--secondary"
                onClick={() => setCopyModalOpen(true)}
                style={{ fontSize: '0.875rem' }}
                disabled={loading}
              >
                Copy
              </button>
            )}
            <button
              id="ads-export-btn"
              className="btn btn--secondary"
              onClick={handleExport}
              style={{ fontSize: '0.875rem' }}
              disabled={loading}
            >
              XLSX
            </button>
            <button
              id="ads-sync-btn"
              className="btn btn--primary"
              disabled={syncing}
              onClick={sync}
              style={{ minWidth: 80 }}
            >
              {syncing ? 'Sync...' : 'Sync'}
            </button>
          </div>
        </div>

        {/* Search input and Active Filters */}
        <div style={{ display: 'flex', gap: 'var(--space-4)', width: '100%', alignItems: 'center' }}>
          <div style={{ flex: '1 1 50%' }}>
            <input
              type="text"
              placeholder="Пошук (кампанія, оголошення, акаунт)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="filter-input"
              style={{ width: '100%', padding: '8px 12px', fontSize: '0.875rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', background: 'var(--color-surface-2)', color: 'var(--color-text)' }}
            />
          </div>
          <div style={{ flex: '1 1 50%', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {statusFilter.length > 0 && (
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                    padding: '4px 10px', background: 'var(--color-primary)', color: '#fff', border: 'none',
                    borderRadius: 'var(--radius-full)', fontSize: '0.75rem', cursor: 'pointer', userSelect: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                  onClick={() => setEditingFilter(editingFilter?.type === 'status' ? null : { type: 'status' })}
                >
                  <span style={{ fontWeight: 600 }}>Статус:</span> {statusFilter.length} обр.
                  <span
                    style={{ marginLeft: '4px', fontWeight: 'bold', opacity: 0.7 }}
                    onClick={(e) => { e.stopPropagation(); setStatusFilter([]); setEditingFilter(null); }}
                  >
                    ✕
                  </span>
                </div>

                {editingFilter?.type === 'status' && (
                  <div
                    className="glass-card"
                    style={{
                      position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 100,
                      padding: 'var(--space-3)', width: 280,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                      display: 'flex', flexDirection: 'column', gap: 'var(--space-2)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-1)' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Редагувати статуси</span>
                      <button className="btn btn--ghost" style={{ padding: '0 4px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setEditingFilter(null)}>✕</button>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {['ACTIVE', 'PAUSED', 'DISAPPROVED', 'BANNED', 'WITH_ISSUES', 'Policy'].map(status => (
                        <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '0.8125rem', cursor: 'pointer', width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) setStatusFilter([...statusFilter, status]);
                              else setStatusFilter(statusFilter.filter(s => s !== status));
                            }}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                    <button className="btn btn--primary" style={{ marginTop: 'var(--space-2)', padding: '4px 8px', fontSize: '0.75rem' }} onClick={() => setEditingFilter(null)}>
                      Готово
                    </button>
                  </div>
                )}
              </div>
            )}

            {metricFilters.map((f, i) => {
              const label = ALL_COLUMNS.find(c => c.key === f.field)?.label || f.field;
              return (
                <div key={i} style={{ position: 'relative' }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                      padding: '4px 10px', background: 'var(--color-primary)', color: '#fff', border: 'none',
                      borderRadius: 'var(--radius-full)', fontSize: '0.75rem', cursor: 'pointer', userSelect: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                    onClick={() => setEditingFilter(editingFilter?.type === 'metric' && editingFilter.index === i ? null : { type: 'metric', index: i })}
                  >
                    <span style={{ fontWeight: 600 }}>{label}</span> {f.operator} {f.value}
                    <span
                      style={{ marginLeft: '4px', fontWeight: 'bold', opacity: 0.7 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMetricFilters(metricFilters.filter((_, idx) => idx !== i));
                        if (editingFilter?.type === 'metric' && editingFilter.index === i) setEditingFilter(null);
                      }}
                    >
                      ✕
                    </span>
                  </div>

                  {editingFilter?.type === 'metric' && editingFilter.index === i && (
                    <div
                      className="glass-card"
                      style={{
                        position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 100,
                        padding: 'var(--space-3)', width: 220,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-1)' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Редагувати фільтр</span>
                        <button className="btn btn--ghost" style={{ padding: '0 4px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setEditingFilter(null)}>✕</button>
                      </div>
                      <select
                        className="filter-select"
                        value={metricFilters[i].field}
                        onChange={(e) => {
                          const newFilters = [...metricFilters];
                          newFilters[i].field = e.target.value;
                          setMetricFilters(newFilters);
                        }}
                        style={{ fontSize: '0.8125rem', padding: '4px 8px' }}
                      >
                        {ALL_COLUMNS.filter(c => ['spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr', 'leads', 'cost_per_lead', 'registrations', 'cost_per_registration', 'purchases', 'cost_per_purchase', 'app_installs', 'cost_per_app_install'].includes(c.key)).map(c => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>

                      <select
                        className="filter-select"
                        value={metricFilters[i].operator}
                        onChange={(e) => {
                          const newFilters = [...metricFilters];
                          newFilters[i].operator = e.target.value as '>=' | '<=' | '=';
                          setMetricFilters(newFilters);
                        }}
                        style={{ fontSize: '0.8125rem', padding: '4px 8px' }}
                      >
                        <option value=">=">{'>='}</option>
                        <option value="<=">{'<='}</option>
                        <option value="=">{'='}</option>
                      </select>

                      <input
                        type="number"
                        className="filter-input"
                        value={metricFilters[i].value}
                        onChange={(e) => {
                          const newFilters = [...metricFilters];
                          newFilters[i].value = Number(e.target.value);
                          setMetricFilters(newFilters);
                        }}
                        style={{ fontSize: '0.8125rem', padding: '4px 8px' }}
                      />
                      <button className="btn btn--primary" style={{ marginTop: 'var(--space-1)', padding: '4px 8px', fontSize: '0.75rem' }} onClick={() => setEditingFilter(null)}>
                        Готово
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Last sync info */}
        {lastSync && (
          <div style={{
            fontSize: '0.75rem', color: 'var(--color-text-muted)',
            flexBasis: '100%',
            borderTop: '1px solid var(--color-border)',
            paddingTop: 'var(--space-2)', marginTop: 'var(--space-1)',
          }}>
            Останній sync: {new Date(lastSync.createdAt).toLocaleString('uk-UA')} ·
            {' '}{lastSync.accountsSuccess ?? 0} OK · {lastSync.accountsFailed ?? 0} помилок
            {(lastSync.durationMs ?? 0) > 0 && ` · ${((lastSync.durationMs ?? 0) / 1000).toFixed(1)}с`}
          </div>
        )}
      </div>

      {/* ── Filters Modal ─────────────────────────────────── */}
      {filtersPanelOpen && (
        <>
          {/* Overlay */}
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000 }}
            onClick={() => setFiltersPanelOpen(false)}
          />

          {/* Modal */}
          <div
            className="glass-card"
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 1001, width: 'min(520px, 90vw)',
              padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Фільтри</h2>
              <button
                className="btn btn--ghost"
                style={{ padding: 'var(--space-1) var(--space-2)', fontSize: '1.25rem', lineHeight: 1 }}
                onClick={() => setFiltersPanelOpen(false)}
              >
                ×
              </button>
            </div>

            {/* Status Filter */}
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 'var(--space-1)' }}>Статус (Status)</div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {['ACTIVE', 'PAUSED', 'DISAPPROVED', 'BANNED', 'WITH_ISSUES', 'Policy'].map(status => (
                  <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: '0.8125rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={statusFilter.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) setStatusFilter([...statusFilter, status]);
                        else setStatusFilter(statusFilter.filter(s => s !== status));
                      }}
                    />
                    {status}
                  </label>
                ))}
              </div>
            </div>

            {/* Metric Filters */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-2)' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 'var(--space-2)' }}>Метрики</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {metricFilters.map((filter, i) => (
                  <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <select
                      className="filter-select"
                      value={filter.field}
                      onChange={(e) => {
                        const newFilters = [...metricFilters];
                        newFilters[i].field = e.target.value;
                        setMetricFilters(newFilters);
                      }}
                      style={{ fontSize: '0.8125rem', minWidth: 150 }}
                    >
                      {ALL_COLUMNS.filter(c => ['spend', 'impressions', 'clicks', 'cpm', 'cpc', 'ctr', 'leads', 'cost_per_lead', 'registrations', 'cost_per_registration', 'purchases', 'cost_per_purchase', 'app_installs', 'cost_per_app_install'].includes(c.key)).map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>

                    <select
                      className="filter-select"
                      value={filter.operator}
                      onChange={(e) => {
                        const newFilters = [...metricFilters];
                        newFilters[i].operator = e.target.value as '>=' | '<=' | '=';
                        setMetricFilters(newFilters);
                      }}
                      style={{ fontSize: '0.8125rem', width: 60 }}
                    >
                      <option value=">=">{'>='}</option>
                      <option value="<=">{'<='}</option>
                      <option value="=">{'='}</option>
                    </select>

                    <input
                      type="number"
                      className="filter-select"
                      value={filter.value}
                      onChange={(e) => {
                        const newFilters = [...metricFilters];
                        newFilters[i].value = Number(e.target.value);
                        setMetricFilters(newFilters);
                      }}
                      style={{ fontSize: '0.8125rem', width: 100, padding: '4px 8px' }}
                    />

                    <button
                      className="btn btn--ghost"
                      style={{ padding: '4px', color: 'var(--color-danger)' }}
                      onClick={() => setMetricFilters(metricFilters.filter((_, idx) => idx !== i))}
                      title="Видалити фільтр"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <button
                className="btn btn--secondary"
                style={{ fontSize: '0.75rem', padding: '4px 10px', marginTop: 'var(--space-2)' }}
                onClick={() => setMetricFilters([...metricFilters, { field: 'purchases', operator: '>=', value: 1 }])}
              >
                + Додати фільтр метрики
              </button>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', justifyContent: 'flex-end', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
              <button
                className="btn btn--ghost"
                onClick={() => { setStatusFilter([]); setMetricFilters([]); }}
              >
                Скинути
              </button>
              <button className="btn btn--primary" onClick={() => setFiltersPanelOpen(false)}>
                Готово
              </button>
            </div>
          </div>
        </>
      )}



      {/* ── Column picker dropdown ────────────────────────── */}
      {colPickerOpen && (
        <div
          className="glass-card"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            marginBottom: 'var(--space-3)',
            display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)',
          }}
        >
          {ALL_COLUMNS.map((col) => (
            <label
              key={col.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                fontSize: '0.8125rem', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={visibleCols.has(col.key)}
                onChange={(e) => toggleCol(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-3)', borderBottom: '1px solid var(--color-border)' }}>
        {[
          { key: 'ads', label: 'По оголошеннях' },
          { key: 'geo', label: 'По GEO' },
          { key: 'accounts', label: 'По кабінетах' },
          { key: 'campaigns', label: 'По кампаніях' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabKey)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 'var(--space-2) 0', fontSize: '0.9375rem', fontWeight: 600,
              color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              transition: 'all var(--transition-fast)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Table ────────────────────────────────────────── */}
      <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <div className="spinner" style={{ width: 32, height: 32, margin: '0 auto var(--space-3)' }} />
            <div>Завантаження...</div>
          </div>
        ) : report.length === 0 ? (
          <div className="empty-state" style={{ padding: 'var(--space-12)' }}>
            <div className="empty-state__title">Немає даних за {dateRange.from === dateRange.to ? dateRange.from : `${dateRange.from} — ${dateRange.to}`}</div>
            <div className="empty-state__desc">Натисни «Синхронізувати» щоб завантажити статистику.</div>
          </div>
        ) : (
          <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.875rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--color-surface-2)' }}>
                <tr>
                  <th
                    style={{
                      padding: 'var(--space-3) var(--space-3)',
                      textAlign: 'center',
                      borderBottom: '1px solid var(--color-border)',
                      width: 40
                    }}
                  >
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          const ids = report.map(r => getActionableFbId(r)).filter((id): id is string => !!id);
                          setSelectedIds(new Set(ids));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      checked={report.length > 0 && selectedIds.size > 0 && selectedIds.size === report.filter(r => getActionableFbId(r)).length}
                      disabled={report.every(r => !getActionableFbId(r))}
                      title={activeTab === 'geo' || activeTab === 'accounts' ? 'Дії недоступні для цього звіту' : undefined}
                    />
                  </th>
                  {visibleColumns.map((col) => {
                    const isCurrencyCol = ['spend', 'cpm', 'cpc', 'cost_per_lead', 'cost_per_registration', 'cost_per_purchase', 'cost_per_app_install', 'budget'].includes(col.key);
                    return (
                      <th
                        key={col.key}
                        onClick={() => col.key !== 'budget' && toggleSort(col.field)}
                        style={{
                          padding: 'var(--space-3) var(--space-3)',
                          textAlign: col.align,
                          fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: sortConfig.field === col.field ? 'var(--color-primary)' : 'var(--color-text-muted)',
                          borderBottom: '1px solid var(--color-border)',
                          cursor: col.key !== 'budget' ? 'pointer' : 'default', whiteSpace: 'nowrap', userSelect: 'none',
                          transition: 'color var(--transition-fast)',
                        }}
                      >
                        {col.label} {isCurrencyCol && currencySymbol ? `(${currencySymbol})` : ''}
                        {col.key !== 'budget' && <SortArrow active={sortConfig.field === col.field} direction={sortConfig.direction} />}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {report.map((row, idx) => (
                  <tr
                    key={`${row.ad_id}-${idx}`}
                    style={{ borderBottom: '1px solid var(--color-border)', transition: 'background var(--transition-fast)' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--color-surface-2)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                  >
                    <td style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const fbId = getActionableFbId(row);
                        return (
                          <input
                            type="checkbox"
                            checked={fbId ? selectedIds.has(fbId) : false}
                            disabled={!fbId}
                            title={!fbId ? 'Немає Facebook ID для цього рядка — дія недоступна' : undefined}
                            onChange={(e) => {
                              if (!fbId) return;
                              const next = new Set(selectedIds);
                              if (e.target.checked) next.add(fbId);
                              else next.delete(fbId);
                              setSelectedIds(next);
                            }}
                          />
                        );
                      })()}
                    </td>
                    {visibleColumns.map((col) => {
                      if (col.key === 'budget') {
                        let displayVal = '—';
                        let canEdit = false;
                        let editType: 'daily' | 'lifetime' = 'daily';
                        let currentBudget = 0;
                        // ВАЖЛИВО: тут потрібен СПРАВЖНІЙ Facebook id, а не row.ad_id
                        // (для campaigns row.ad_id — синтетичний ключ, іноді просто назва
                        // кампанії, якщо campaign_id з бекенду прийшов null — саме це
                        // спричиняло помилку "Object with ID ... does not exist").
                        let editId: string | null = row.ad_id; // для ads — це справжній ad_id, ок
                        let editMatchField: 'campaign_id' | 'adset_id' = 'adset_id';

                        if (activeTab === 'campaigns') {
                          editId = row.campaign_id ?? null;
                          editMatchField = 'campaign_id';
                          if (row.campaign_budget && editId) {
                            displayVal = fmtMoney(row.campaign_budget);
                            canEdit = true;
                            editType = row.campaign_budget_type as 'daily' | 'lifetime';
                            currentBudget = row.campaign_budget;
                          } else if (row.campaign_budget && !editId) {
                            // Є бюджет у даних, але немає справжнього campaign_id —
                            // редагування свідомо забороняємо, щоб не відправити
                            // назву кампанії в Graph API як id.
                            displayVal = fmtMoney(row.campaign_budget);
                            canEdit = false;
                          } else {
                            displayVal = 'ABO';
                          }
                        } else if (activeTab === 'ads') {
                          if (row.campaign_budget) {
                            displayVal = 'CBO';
                          } else if (row.adset_budget) {
                            displayVal = fmtMoney(row.adset_budget);
                            editType = row.adset_budget_type as 'daily' | 'lifetime';
                            currentBudget = row.adset_budget;
                            editMatchField = 'adset_id';
                            if (row.adset_id) {
                              editId = row.adset_id;
                              canEdit = true;
                            } else {
                              // Немає справжнього adset_id — НЕ підставляємо ad_id замість
                              // нього (ad_id ≠ adset_id для Graph API), просто забороняємо editing.
                              editId = null;
                              canEdit = false;
                            }
                          } else {
                            displayVal = '—';
                          }
                        }

                        return (
                          <td key={col.key} style={{ padding: 'var(--space-2) var(--space-3)', textAlign: col.align, verticalAlign: 'middle', position: 'relative' }} className="budget-cell">
                            {editingBudgetRowId === editId ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                <input
                                  type="number"
                                  value={budgetInputValue}
                                  onChange={(e) => setBudgetInputValue(e.target.value)}
                                  className="filter-input"
                                  style={{ width: 80, padding: '2px 4px', fontSize: '0.8125rem' }}
                                  autoFocus
                                />
                                <button className="btn btn--ghost" style={{ padding: '2px', color: 'var(--color-success)' }} onClick={() => editId && handleBudgetSave(editId, editType, editMatchField)}>✓</button>
                                <button className="btn btn--ghost" style={{ padding: '2px', color: 'var(--color-danger)' }} onClick={() => setEditingBudgetRowId(null)}>✕</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                <span
                                  style={{ fontVariantNumeric: 'tabular-nums', fontWeight: canEdit ? 600 : 400 }}
                                  title={!canEdit && !editId && displayVal !== 'ABO' && displayVal !== '—' ? 'Немає справжнього campaign_id від Facebook — редагування недоступне' : undefined}
                                >
                                  {displayVal}
                                </span>
                                {canEdit && editId && (
                                  <button
                                    className="btn btn--ghost edit-btn"
                                    style={{ padding: '2px', opacity: 0.5 }}
                                    onClick={() => {
                                      setEditingBudgetRowId(editId);
                                      setBudgetInputValue(String(currentBudget));
                                    }}
                                  >
                                    ✎
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td
                          key={col.key}
                          style={{ padding: 'var(--space-2) var(--space-3)', textAlign: col.align, verticalAlign: 'middle' }}
                        >
                          {col.render(row)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>

              {/* Totals footer */}
              <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 10, background: 'var(--color-surface-2)' }}>
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ borderTop: '2px solid var(--color-border)' }}></td>
                  {visibleColumns.map((col) => (
                    <td
                      key={col.key}
                      style={{ padding: 'var(--space-3) var(--space-3)', textAlign: col.align, verticalAlign: 'middle', borderTop: '2px solid var(--color-border)' }}
                    >
                      {totals[col.key]}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Account filter modal ──────────────────────────── */}
      <AccountFilterModal
        isOpen={filterModalOpen}
        currentFilter={accountFilter}
        onApply={setAccountFilter}
        onClose={() => setFilterModalOpen(false)}
      />

      <CopyForSheetsModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        report={report}
      />
    </div>
  );
}